import { AudioPosition } from '@/types/audioPosition';
import { authService } from './authService';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';
const PROGRESS_BASE = `${API_BASE_URL}/api/progress`;

/** A single book's progress as stored on the server. */
export interface ServerProgress {
  book_id: string;
  total_progress: number;
  position: number;
  /** Client playback timestamp (epoch ms); authoritative for last-write-wins. */
  last_played: number | null;
  /** Rest of the AudioPosition payload (sentenceIndex, textFingerprint, rating, …). */
  state: Partial<AudioPosition> | null;
}

/** A local cookie entry uploaded during bulk sync/migration. */
export type ProgressSyncEntry = Partial<AudioPosition> & { book_id: string };

/**
 * Fetch all progress rows for the current user, newest first.
 * Caller is responsible for ensuring the user is authenticated.
 */
export async function fetchAllProgress(): Promise<ServerProgress[]> {
  const res = await authService.makeAuthenticatedRequest(PROGRESS_BASE, { method: 'GET' });
  if (!res.ok) {
    throw new Error(`Failed to fetch progress: ${res.status} ${res.statusText}`);
  }
  return res.json();
}

/** Upsert one book's progress (last-write-wins by the position's `lastPlayed`). */
export async function putProgress(bookId: string, position: AudioPosition): Promise<ServerProgress> {
  const res = await authService.makeAuthenticatedRequest(
    `${PROGRESS_BASE}/${encodeURIComponent(bookId)}`,
    {
      method: 'PUT',
      body: JSON.stringify(position),
      // Survive page-hide / unload so the final flush still reaches the server.
      keepalive: true,
    },
  );
  if (!res.ok) {
    throw new Error(`Failed to save progress for ${bookId}: ${res.status} ${res.statusText}`);
  }
  return res.json();
}

/** Remove a book's progress row for the current user. */
export async function deleteProgress(bookId: string): Promise<void> {
  const res = await authService.makeAuthenticatedRequest(
    `${PROGRESS_BASE}/${encodeURIComponent(bookId)}`,
    { method: 'DELETE' },
  );
  if (!res.ok && res.status !== 204) {
    throw new Error(`Failed to delete progress for ${bookId}: ${res.status} ${res.statusText}`);
  }
}

/**
 * Bulk-merge local cookie entries into the server (last-write-wins each) and
 * return the authoritative merged set. Used for one-time cookie->server
 * migration and periodic reconciliation.
 */
export async function syncProgress(entries: ProgressSyncEntry[]): Promise<ServerProgress[]> {
  const res = await authService.makeAuthenticatedRequest(`${PROGRESS_BASE}/sync`, {
    method: 'POST',
    body: JSON.stringify(entries),
  });
  if (!res.ok) {
    throw new Error(`Failed to sync progress: ${res.status} ${res.statusText}`);
  }
  return res.json();
}
